import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toaster';
import { portalReportsApi } from '../../api/portal-reports';
import { SparklesIcon } from '@heroicons/react/24/outline';
import type { PortalReport } from '@affogato/shared';

interface GenerateReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
  onGenerated: (report: PortalReport) => void;
}

export function GenerateReportModal({
  isOpen,
  onClose,
  clientId,
  clientName,
  onGenerated,
}: GenerateReportModalProps) {
  const { t, i18n } = useTranslation('clients');
  const [instructions, setInstructions] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await portalReportsApi.generate(clientId, {
        instructions: instructions.trim() || undefined,
        locale: i18n.language,
      });

      toast.success(t('reports.generated'));
      onGenerated(response.report);
      handleClose();
    } catch (error: any) {
      if (error.message?.includes('AI_NOT_CONFIGURED')) {
        toast.error(t('reports.errors.notConfigured'));
      } else if (error.message?.includes('RATE_LIMITED')) {
        toast.error(t('reports.errors.rateLimited'));
      } else {
        toast.error(error.message || t('reports.errors.generateFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setInstructions('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('reports.generateTitle')}
      description={t('reports.generateDescription', { name: clientName })}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            {t('reports.instructionsLabel')}
          </label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder={t('reports.instructionsPlaceholder')}
            rows={4}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
            {t('reports.instructionsHint')}
          </p>
        </div>

        <div className="flex items-center gap-2 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
          <SparklesIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <p className="text-sm text-primary-700 dark:text-primary-300">
            {t('reports.aiNote')}
          </p>
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            className="flex-1"
            disabled={isLoading}
          >
            {t('common:cancel')}
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            className="flex-1"
          >
            <SparklesIcon className="w-4 h-4 mr-2" />
            {t('reports.generateButton')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
